import { Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import type { TabularDataCell } from "../../../../../../api/types/svTypes.ts";
import useAgingSummaryContext from "../contexts/AgingSummaryContext.tsx";

const COUNTERPARTY_GUID = "ApArCounterpartyName";
const ENTITY_NAME_GUID = "EntityName";
const TOTALS_ROW_ID = "__totals__";
const DIMENSION_GUIDS = new Set([COUNTERPARTY_GUID, ENTITY_NAME_GUID]);

interface Props {
  onDrillDown?: (cell: TabularDataCell) => void;
}

interface GridRow {
  id: string;
  data: Record<string, TabularDataCell>;
  isTotal: boolean;
}

const AgingSummaryTable = ({ onDrillDown }: Props) => {
  const { report, sortModel, onSortModelChange } = useAgingSummaryContext();

  const valueColumns = useMemo(
    () => (report?.columns ?? []).filter((col) => !DIMENSION_GUIDS.has(col.guid)),
    [report?.columns]
  );

  const entityNameColumn = useMemo(
    () => (report?.columns ?? []).find((col) => col.guid === ENTITY_NAME_GUID),
    [report?.columns]
  );

  const gridRows = useMemo<GridRow[]>(() => {
    const dataRows: GridRow[] = (report?.rows ?? []).map((row) => ({
      id: row.id,
      data: row.data,
      isTotal: false,
    }));

    if (report?.totals && Object.keys(report.totals).length > 0) {
      dataRows.push({ id: TOTALS_ROW_ID, data: report.totals, isTotal: true });
    }

    return dataRows;
  }, [report]);

  const columns = useMemo<GridColDef<GridRow>[]>(() => {
    const counterpartyCol: GridColDef<GridRow> = {
      field: COUNTERPARTY_GUID,
      headerName: "Counterparty",
      minWidth: 200,
      width: 300,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (_value: unknown, row: GridRow) => row.data[COUNTERPARTY_GUID]?.formattedValue ?? "",
      renderCell: (params: GridRenderCellParams<GridRow>) => (
        <TypographyTooltipEllipsis
          text={params.value}
          typographyProps={{ variant: params.row.isTotal ? "subtitle2" : "body1" }}
        />
      ),
    };

    const valueCols: GridColDef<GridRow>[] = valueColumns.map((col) => ({
      field: col.guid,
      headerName: col.title,
      minWidth: 130,
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
      headerAlign: "right" as const,
      align: "right" as const,
      valueGetter: (_value: unknown, row: GridRow) => row.data[col.guid] ?? null,
      sortComparator: (v1: TabularDataCell | null, v2: TabularDataCell | null) => {
        const a = v1?.value != null ? Number(v1.value) : null;
        const b = v2?.value != null ? Number(v2.value) : null;
        if (a === null && b === null) return 0;
        if (a === null) return 1;
        if (b === null) return -1;
        return a - b;
      },
      renderCell: (params: GridRenderCellParams<GridRow, TabularDataCell | null>) => {
        const cell = params.value;
        const canDrillDown = !!cell?.drillDownId;
        const formatted = cell?.formattedValue || "-";
        return (
          <Typography
            variant={params.row.isTotal ? "subtitle2" : "body1"}
            sx={{
              ...(canDrillDown ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}),
            }}
            onClick={() => canDrillDown && cell && onDrillDown?.(cell)}
          >
            {formatted}
          </Typography>
        );
      },
    }));

    const cols: GridColDef<GridRow>[] = [counterpartyCol];

    if (entityNameColumn) {
      cols.push({
        field: ENTITY_NAME_GUID,
        headerName: entityNameColumn.title,
        minWidth: 200,
        width: 250,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_value: unknown, row: GridRow) => row.data[ENTITY_NAME_GUID]?.formattedValue ?? "",
        renderCell: (params: GridRenderCellParams<GridRow>) => (
          <TypographyTooltipEllipsis
            text={params.value}
            typographyProps={{ variant: params.row.isTotal ? "subtitle2" : "body1" }}
          />
        ),
      });
    }

    cols.push(...valueCols);
    return cols;
  }, [valueColumns, entityNameColumn, onDrillDown]);

  return (
    <DataGridPremium<GridRow>
      rows={gridRows}
      columns={columns}
      disableRowSelectionOnClick
      hideFooter
      columnHeaderHeight={36}
      rowHeight={36}
      showCellVerticalBorder
      showColumnVerticalBorder
      pinnedRows={{ bottom: gridRows.filter((r) => r.isTotal) }}
      pinnedColumns={{ left: [COUNTERPARTY_GUID] }}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      getRowClassName={(params) => (params.row.isTotal ? "aging-totals-row" : "")}
      sx={(theme) => ({
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        "& .MuiDataGrid-columnHeaderTitle": { color: "text.secondary", ...theme.typography.caption },
        "& .aging-totals-row, & .aging-totals-row .MuiDataGrid-cell": { backgroundColor: "background.grey" },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" },
        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
        "& .MuiDataGrid-filler": { borderTop: `1px solid ${theme.palette.divider}` },
        "& .MuiDataGrid-filler--pinnedLeft": { borderRight: `1px solid ${theme.palette.divider}` },
      })}
    />
  );
};

export default AgingSummaryTable;
